/**
 * Lead Action Feed — Admin Dashboard
 *
 * Event-day monitoring tool: paginated, filtered feed of lead_actions
 * with auto-refresh when an event filter is active.
 *
 * API Endpoints:
 * - GET /api/v1/admin/lead-actions   — List with filters + pagination
 * - POST /api/v1/admin/lead-actions/:id/retry-hubspot — Retry HubSpot sync
 *
 * @story SCRUM-1473 — 0.1.1.10-05: Lead Action Feed
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import apiRequest from '../../services/api';

function useCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    apiRequest('/api/v1/admin/campaigns?limit=100')
      .then((res) => {
        if (res.success) {
          setCampaigns(res.data || []);
        }
      })
      .catch(() => {});
  }, []);
  return campaigns;
}

const ACTION_TYPE_BADGES = {
  question_response: { label: 'Question / Response', color: '#fff', bg: '#f59e0b' },
  signup_request: { label: 'Signup', color: '#fff', bg: '#8b5cf6' },
  rsvp: { label: 'RSVP', color: '#fff', bg: '#3b82f6' },
  checkin: { label: 'Check-in', color: '#fff', bg: '#10b981' },
  walk_in: { label: 'Walk-in', color: '#fff', bg: '#f97316' },
};

const AUTO_REFRESH_MS = 30000;

function formatTime(dateStr) {
  if (!dateStr) {
    return 'N/A';
  }
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) {
    return 'just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  return d.toLocaleDateString();
}

// ─── Summary Counters ──────────────────────────────────────────────

function SummaryCounters({ data }) {
  if (!data || data.length === 0) {
    return null;
  }
  const counts = {};
  for (const row of data) {
    counts[row.action_type] = (counts[row.action_type] || 0) + 1;
  }
  return (
    <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
      {Object.entries(counts).map(([type, count]) => {
        const badge = ACTION_TYPE_BADGES[type] || { label: type, bg: '#6b7280' };
        return (
          <div key={type} style={{
            padding: '8px 16px', borderRadius: '8px', border: `2px solid ${badge.bg}`,
            textAlign: 'center', minWidth: '80px',
          }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: badge.bg }}>{count}</div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>{badge.label}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Filter Bar ────────────────────────────────────────────────────

function FilterBar({ filters, onFilterChange, campaigns }) {
  const inputStyle = { padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' };
  return (
    <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
      <select
        value={filters.action_type || ''}
        onChange={(e) => onFilterChange({ ...filters, action_type: e.target.value || undefined })}
        style={inputStyle}
      >
        <option value="">All Types</option>
        <option value="question_response">Question / Response</option>
        <option value="signup_request">Signup Request</option>
        <option value="rsvp">RSVP</option>
        <option value="checkin">Check-in</option>
        <option value="walk_in">Walk-in</option>
      </select>

      <select
        value={filters.campaign_id || ''}
        onChange={(e) => onFilterChange({ ...filters, campaign_id: e.target.value || undefined })}
        style={inputStyle}
      >
        <option value="">All Campaigns</option>
        {campaigns.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>

      <input
        type="text"
        placeholder="Event ID"
        value={filters.event_id || ''}
        onChange={(e) => onFilterChange({ ...filters, event_id: e.target.value || undefined })}
        style={{ ...inputStyle, width: '280px' }}
      />

      <input
        type="text"
        placeholder="Market ID"
        value={filters.market_id || ''}
        onChange={(e) => onFilterChange({ ...filters, market_id: e.target.value || undefined })}
        style={{ ...inputStyle, width: '280px' }}
      />

      <select
        value={filters.hubspot_synced === undefined ? '' : String(filters.hubspot_synced)}
        onChange={(e) => {
          const val = e.target.value;
          onFilterChange({
            ...filters,
            hubspot_synced: val === '' ? undefined : val === 'true',
          });
        }}
        style={inputStyle}
      >
        <option value="">HubSpot: All</option>
        <option value="true">Synced</option>
        <option value="false">Not Synced</option>
      </select>

      {filters.event_id && (
        <span style={{
          padding: '4px 10px', borderRadius: '12px', fontSize: '11px',
          backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600,
        }}>
          Auto-refresh ON (30s)
        </span>
      )}
    </div>
  );
}

// ─── Expanded Row ──────────────────────────────────────────────────

function ExpandedRow({ row }) {
  return (
    <tr>
      <td colSpan={7} style={{ padding: '12px 16px', backgroundColor: '#f9fafb', fontSize: '12px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <div><strong>Lead ID:</strong> {row.lead_id || 'N/A'}</div>
          <div><strong>HubSpot Contact:</strong> {row.hubspot_contact_id || 'N/A'}</div>
          <div><strong>Event:</strong> {row.event_name || row.event_id || 'N/A'}</div>
          <div><strong>Intent Code:</strong> {row.intent_code_label || row.intent_code_id || 'N/A'}</div>
          <div><strong>Market:</strong> {row.market_name || row.market_id || 'N/A'}</div>
          <div><strong>Campaign:</strong> {row.campaign_name || 'N/A'}</div>
          <div><strong>Synced At:</strong> {row.hubspot_synced_at ? new Date(row.hubspot_synced_at).toLocaleString() : 'N/A'}</div>
          {row.hubspot_sync_error && (
            <div style={{ gridColumn: '1 / -1', color: '#dc2626' }}>
              <strong>Sync Error:</strong> {row.hubspot_sync_error}
            </div>
          )}
          {row.metadata && Object.keys(row.metadata).length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <strong>Metadata:</strong>
              <pre style={{ margin: '4px 0', padding: '8px', backgroundColor: '#fff', borderRadius: '4px', overflow: 'auto', maxHeight: '120px' }}>
                {JSON.stringify(row.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main Component ────────────────────────────────────────────────

export default function LeadActionFeed() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState(null);
  const intervalRef = useRef(null);
  const campaigns = useCampaigns();

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (filters.action_type) {
        params.set('action_type', filters.action_type);
      }
      if (filters.event_id) {
        params.set('event_id', filters.event_id);
      }
      if (filters.market_id) {
        params.set('market_id', filters.market_id);
      }
      if (filters.campaign_id) {
        params.set('campaign_id', filters.campaign_id);
      }
      if (filters.hubspot_synced !== undefined) {
        params.set('hubspot_synced', String(filters.hubspot_synced));
      }

      const response = await apiRequest(`/api/v1/admin/lead-actions?${params.toString()}`);
      if (response.success) {
        setData(response.data);
        setTotal(response.total);
        setHasMore(response.has_more);
        setError(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load lead actions');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // Auto-refresh when event_id filter is active
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (filters.event_id) {
      intervalRef.current = setInterval(fetchData, AUTO_REFRESH_MS);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [filters.event_id, fetchData]);

  const handleRetryHubspot = async (id) => {
    try {
      await apiRequest(`/api/v1/admin/lead-actions/${id}/retry-hubspot`, { method: 'POST' });
      setToast('HubSpot retry enqueued');
      setTimeout(() => setToast(null), 3000);
      fetchData();
    } catch (err) {
      setToast(`Retry failed: ${err.message}`);
      setTimeout(() => setToast(null), 5000);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="lead-actions"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null
      }}
    >
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>Lead Action Feed</h1>

      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', padding: '12px 20px',
          borderRadius: '8px', backgroundColor: toast.startsWith('Retry failed') ? '#fee2e2' : '#d1fae5',
          color: toast.startsWith('Retry failed') ? '#dc2626' : '#065f46',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, fontSize: '14px',
        }}>
          {toast}
        </div>
      )}

      <FilterBar filters={filters} onFilterChange={handleFilterChange} campaigns={campaigns} />

      {filters.event_id && <SummaryCounters data={data} />}

      {error && (
        <div style={{ padding: '16px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading && data.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      ) : data.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>No lead actions found.</div>
      ) : (
        <>
          <div style={{ marginBottom: '8px', fontSize: '13px', color: '#6b7280' }}>
            {total} total — page {page} of {totalPages}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>Type</th>
                <th style={{ padding: '8px' }}>Email</th>
                <th style={{ padding: '8px' }}>Event</th>
                <th style={{ padding: '8px' }}>Market</th>
                <th style={{ padding: '8px' }}>HubSpot</th>
                <th style={{ padding: '8px' }}>When</th>
                <th style={{ padding: '8px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const badge = ACTION_TYPE_BADGES[row.action_type] || { label: row.action_type, bg: '#6b7280', color: '#fff' };
                const isExpanded = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', backgroundColor: isExpanded ? '#f0fdf4' : 'transparent' }}
                      onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    >
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                          backgroundColor: badge.bg, color: badge.color,
                        }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: '8px', fontSize: '13px' }}>{row.masked_email || '—'}</td>
                      <td style={{ padding: '8px', fontSize: '13px' }}>{row.event_name || '—'}</td>
                      <td style={{ padding: '8px', fontSize: '13px' }}>{row.market_name || '—'}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                          backgroundColor: row.hubspot_synced ? '#d1fae5' : '#fee2e2',
                          color: row.hubspot_synced ? '#065f46' : '#dc2626',
                        }}>
                          {row.hubspot_synced ? 'Synced' : 'Pending'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', fontSize: '12px', color: '#6b7280' }}>{formatTime(row.created_at)}</td>
                      <td style={{ padding: '8px' }} onClick={(e) => e.stopPropagation()}>
                        {!row.hubspot_synced && (
                          <button
                            onClick={() => handleRetryHubspot(row.id)}
                            style={{
                              padding: '4px 10px', fontSize: '11px', borderRadius: '4px',
                              border: '1px solid #3b82f6', backgroundColor: '#eff6ff', color: '#3b82f6',
                              cursor: 'pointer',
                            }}
                          >
                            Retry Sync
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && <ExpandedRow row={row} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center' }}>
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              style={{
                padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db',
                cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1,
              }}
            >
              Prev
            </button>
            <span style={{ padding: '6px 12px', fontSize: '13px', color: '#6b7280' }}>
              {page} / {totalPages}
            </span>
            <button
              disabled={!hasMore}
              onClick={() => setPage(page + 1)}
              style={{
                padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db',
                cursor: !hasMore ? 'not-allowed' : 'pointer', opacity: !hasMore ? 0.5 : 1,
              }}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
    </DashboardLayout>
  );
}
